import useTranslation from '../../../localization/i18n.context';
import CardStatus from '../../../constants/card.status';
import Alert from '../../shared/Alert/Alert';
import CheckIcon from '../../shared/Icons/CheckIcon';
import NavigationUtils from '../../../utils/navigation.utils';
import CreditStatus from '../../../constants/credit.status';
import CoinsImage from '../../../assets/images/coins-alert.webp';
import CardMessage from './CardMessage';
import FormatterUtils from '../../../utils/formatter.utils';
import { EnterAmountType } from '../../../routes/EnterAmountPage/EnterAmountProvider';
import PaymentStatus from '../../../constants/payment.status';

export const CardValidStatus = [
  CardStatus.ISSUED,
  CardStatus.INCOMPLETE,
  CardStatus.UNDER_REVIEW,
  CardStatus.REJECTED,
  CardStatus.APPROVED,
  CardStatus.ACTIVATION_IN_PROGRESS,
  CardStatus.SET_PIN,
  CardStatus.MANUAL_ACTIVATION,
];

export const CreditValidStatus = [
  CreditStatus.NOT_APPLIED,
  CreditStatus.APPROVED,
  CreditStatus.PENDING_APPROVAL,
  CreditStatus.REJECTED,
];

export const CreditStatusMessage = ({
  status,
  size = CardMessage.Size.LARGE,
  availableAmount = 0,
  payment = null,
  navigate,
  className = '',
  automaticLendingEnabled = false,
}) => {
  const { t, getCurrency } = useTranslation();

  const messages = {
    [CreditStatus.NOT_APPLIED]: (
      <Alert
        hasIcon
        className={className}
        size={size}
        type={Alert.Type.MARKETING}
        icon={<img src={CoinsImage} width={40} height={40} />}
        message={t('Get approved for credit limit. Receive up to {amount}', {
          '{amount}': FormatterUtils.currency(availableAmount, getCurrency()),
        })}
        hasButton
        buttonText={t('Apply')}
        onButtonClick={() => {
          navigate(NavigationUtils.Routes.CREDIT_ONBOARDING);
        }}
      />
    ),

    [CreditStatus.APPROVED]: (
      <Alert
        hasIcon
        className={className}
        size={size}
        type={
          automaticLendingEnabled ? Alert.Type.MARKETING : Alert.Type.SUCCESS
        }
        icon={
          automaticLendingEnabled ? (
            <img src={CoinsImage} width={40} height={40} />
          ) : (
            <CheckIcon width={24} height={24} />
          )
        }
        message={
          automaticLendingEnabled
            ? t(
                'Your pre-approved credit will be soon. Please check back later',
              )
            : t('Credit approved! Activate it to start using it')
        }
        hasButton={!automaticLendingEnabled}
        buttonText={t('Activate')}
        onButtonClick={() => {
          navigate(NavigationUtils.Routes.PICKUP);
        }}
      />
    ),

    [CreditStatus.ACTIVE]: (
      <Alert
        hasIcon
        className={className}
        size={size}
        type={Alert.Type.SUCCESS}
        message={t('Credit loaned! {amount} credit limit', {
          '{amount}': FormatterUtils.currency(availableAmount, getCurrency()),
        })}
        hasButton
        buttonText={t('Use credit')}
        onButtonClick={() => {
          navigate(NavigationUtils.Routes.ENTER_AMOUNT, false, {
            type: EnterAmountType.MICROLOAN,
          });
        }}
      />
    ),

    [CreditStatus.PENDING_APPROVAL]: (
      <Alert
        hasIcon
        className={className}
        size={size}
        type={Alert.Type.INFO}
        message={t('Credit application under review. Please check back later')}
      />
    ),

    [CreditStatus.USED]: (() => {
      if (payment?.status === PaymentStatus.OVERDUE) {
        return (
          <Alert
            hasIcon
            className={className}
            size={size}
            type={Alert.Type.ERROR}
            message={
              <span>
                {t('Account blocked')}
                <br />
                {t('Overdue credit payment')}
              </span>
            }
            hasButton
            buttonText={t('Pay')}
            onButtonClick={() => {
              navigate(NavigationUtils.Routes.WALLET);
            }}
          />
        );
      }
      if (payment?.amount === 0) return <></>;
      return (
        <Alert
          hasIcon
          className={className}
          size={size}
          type={Alert.Type.INFO}
          message={
            <span>
              {t('New payment cycle available')}
              <br />
              {t('Amount to pay: {amount}', {
                '{amount}': FormatterUtils.currency(payment?.amount),
              })}
            </span>
          }
          hasButton
          buttonText={t('Pay')}
          onButtonClick={() => {
            navigate(NavigationUtils.Routes.WALLET);
          }}
        />
      );
    })(),

    [CreditStatus.BLOCKED]: (
      <Alert
        hasIcon
        className={className}
        size={size}
        type={Alert.Type.ERROR}
        message={
          <span>
            {t('Account blocked')}
            <br />
            {t('Overdue credit payment')}
          </span>
        }
      />
    ),

    [CreditStatus.REJECTED]: (
      <Alert
        hasIcon
        className={className}
        size={size}
        type={Alert.Type.ERROR}
        message={
          <span>
            {t('Account blocked')}
            <br />
            {t('Overdue credit payment')}
          </span>
        }
      />
    ),
  };

  return messages[status] ?? <></>;
};

export const CardStatusMessage = ({
  status,
  size = CardMessage.Size.LARGE,
  navigate,
  className = '',
  isPersonalizedCard = false,
  automaticLendingEnabled = false,
}) => {
  const { t } = useTranslation();

  // ATTENTION: This is a workaround for personalized cards. We need to be sure that if card was not issue but application approved, the label is shown correctly as an issued card
  if (status === CardStatus.APPROVED && isPersonalizedCard) {
    status = CardStatus.ISSUED;
  }

  const messages = {
    [CardStatus.INCOMPLETE]: (
      <Alert
        hasIcon
        className={className}
        size={size}
        type={Alert.Type.WARNING}
        message={t('Incomplete application. Re-enter your information.')}
        hasButton
        buttonText={t('Go')}
        onButtonClick={() => {
          navigate(NavigationUtils.Routes.ONBOARDING);
        }}
      />
    ),

    [CardStatus.UNDER_REVIEW]: (
      <Alert
        hasIcon
        className={className}
        size={size}
        type={Alert.Type.INFO}
        message={t('Application under review. Please check back later.')}
      />
    ),

    [CardStatus.REJECTED]: (
      <Alert
        hasIcon
        className={className}
        size={size}
        type={Alert.Type.INFO}
        message={t('Problems with your application')}
        hasButton
        buttonText={t('Go')}
        onButtonClick={() => {
          navigate(NavigationUtils.Routes.ONBOARDING);
        }}
      />
    ),

    [CardStatus.ISSUED]: (
      <Alert
        hasIcon
        className={className}
        size={size}
        type={Alert.Type.SUCCESS}
        message={
          automaticLendingEnabled
            ? t('Activate your card to use your pre-approved credit')
            : t('Card approved! Activate now')
        }
        hasButton
        buttonText={t('Activate')}
        onButtonClick={() => {
          navigate(NavigationUtils.Routes.CARD_ACTIVATION);
        }}
      />
    ),

    [CardStatus.APPROVED]: (
      <Alert
        hasIcon
        className={className}
        size={size}
        type={Alert.Type.SUCCESS}
        message={
          automaticLendingEnabled
            ? t('Activate your card to use your pre-approved credit')
            : t('Card approved! Pick up and activate now')
        }
        hasButton
        buttonText={t('Activate')}
        onButtonClick={() => {
          navigate(NavigationUtils.Routes.PICKUP);
        }}
      />
    ),

    [CardStatus.ACTIVATION_IN_PROGRESS]: (
      <Alert
        hasIcon
        className={className}
        size={size}
        type={Alert.Type.INFO}
        message={t('Activation in progress. It can take up to 30 minutes.')}
      />
    ),

    [CardStatus.MANUAL_ACTIVATION]: (
      <Alert
        hasIcon
        className={className}
        size={size}
        type={Alert.Type.INFO}
        message={t('Activation in progress. It can take up to 72hs')}
      />
    ),

    [CardStatus.SET_PIN]: (
      <Alert
        hasIcon
        className={className}
        size={size}
        type={Alert.Type.WARNING}
        message={
          automaticLendingEnabled
            ? t(
                'Complete your activation! Set your pin now to use your pre-approved credit.',
              )
            : t('Complete your activation! Set your pin now.')
        }
        hasButton
        buttonText={t('Set pin')}
        onButtonClick={() => {
          navigate(NavigationUtils.Routes.SET_PIN);
        }}
      />
    ),
  };

  return messages[status] ?? <></>;
};
